import React, { useState, useEffect } from 'react';
import { Button, Table, Space, Modal, Tag, message, Alert, Empty, Row, Col, Input, Select, Form } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, TeamOutlined, RobotOutlined } from '@ant-design/icons';
import apiClient from '../api';

const GroupMappingManager = ({ templates, loading, onUpdate }) => {
  const [mappings, setMappings] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    groupId: '',
    groupName: '',
    templateId: ''
  });

  useEffect(() => {
    loadMappings();
  }, []);

  const loadMappings = async () => {
    try {
      const response = await apiClient.get('/group-mappings');
      if (response.data.success) {
        setMappings(response.data.mappings || []);
      }
    } catch (error) {
      console.error('Error loading group mappings:', error);
    }
  };

  const handleCreate = () => {
    setEditingMapping(null);
    setFormData({ groupId: '', groupName: '', templateId: '' });
    setIsModalVisible(true);
  };

  const handleEdit = (mapping) => {
    setEditingMapping(mapping);
    setFormData({
      groupId: mapping.groupId,
      groupName: mapping.groupName || '',
      templateId: mapping.templateId
    });
    setIsModalVisible(true);
  };

  const handleDelete = (mapping) => {
    if (!mapping) return;
    console.log('[GroupMappingManager] Delete clicked for mapping:', mapping);
    setDeleteTarget(mapping);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const groupId = deleteTarget.groupId;
    if (!groupId) return;

    try {
      const response = await apiClient.delete(
        `/group-mappings/${encodeURIComponent(groupId)}`
      );
      if (response.data.success) {
        message.success('Group mapping deleted successfully');
        setDeleteTarget(null);
        loadMappings();
        if (onUpdate) onUpdate();
      } else {
        message.error(response.data.message || 'Failed to delete mapping');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete mapping');
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.groupId || !formData.templateId) {
      message.error('Please fill in all required fields');
      return;
    }

    try {
      const url = editingMapping 
        ? `/group-mappings/${encodeURIComponent(editingMapping.groupId)}`
        : '/group-mappings';
      
      const method = editingMapping ? 'put' : 'post';
      const response = await apiClient[method](url, formData);

      if (response.data.success) {
        message.success(`Group mapping ${editingMapping ? 'updated' : 'created'} successfully`);
        setIsModalVisible(false);
        loadMappings();
        if (onUpdate) onUpdate();
      } else {
        message.error(response.data.message || 'Failed to save mapping');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save mapping');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Group ID',
      dataIndex: 'groupId',
      key: 'groupId',
      width: 300,
      render: (text) => (
        <Space>
          <TeamOutlined style={{ color: '#667eea' }} />
          <Tag color="blue" style={{ fontSize: 12, fontFamily: 'monospace' }}>
            {text.length > 30 ? `${text.substring(0, 30)}...` : text}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Group Name',
      dataIndex: 'groupName',
      key: 'groupName',
      width: 200,
      render: (text) => text || <Tag color="default">Unnamed</Tag>,
    },
    {
      title: 'Assigned Agent',
      dataIndex: 'templateId',
      key: 'templateId',
      render: (templateId) => {
        const template = templates.find(t => t.templateId === templateId);
        return (
          <Space direction="vertical" size={2}>
            <Tag color="green" icon={<RobotOutlined />}>
              {templateId}
            </Tag>
            {template && (
              <span style={{ fontSize: 12, color: '#666' }}>
                {template.agentName}
              </span>
            )}
            {!template && (
              <Tag color="red">Template not found</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button 
            type="link" 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <h2 style={{ margin: 0 }}>👥 Group-to-Agent Mappings</h2>
            <p style={{ color: '#666', margin: '4px 0 0 0' }}>
              Assign specific agents to WhatsApp groups (master agent system)
            </p>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              size="large"
              onClick={handleCreate}
              disabled={templates.length === 0}
            >
              Map Group
            </Button>
          </Col>
        </Row>
      </div>

      <Alert
        message="How it works"
        description={
          <div>
            <p style={{ marginBottom: 8 }}>
              <strong>Map WhatsApp groups to specific agents</strong>
            </p>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>Group "Support Team" → Support Agent</li>
              <li>Group "Sales Team" → Sales Agent</li>
            </ul>
            <p style={{ marginTop: 8, marginBottom: 0 }}>
              💡 All group messages go to one master bot, but different groups get different agents
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Delete confirmation modal */}
      <Modal
        title="Delete Group Mapping"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={handleConfirmDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <p>Are you sure you want to remove this group mapping? This action cannot be undone.</p>
        {deleteTarget && (
          <p>
            <strong>Group ID:</strong>{' '}
            <Tag color="blue" style={{ marginLeft: 4 }}>
              {deleteTarget.groupId}
            </Tag>
          </p>
        )}
      </Modal>

      {templates.length === 0 && (
        <Alert
          message="No Templates Available"
          description="You need to create at least one agent template before creating mappings."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table 
        columns={columns} 
        dataSource={mappings}
        rowKey="groupId"
        loading={loading}
        pagination={{ pageSize: 10 }}
        locale={{
          emptyText: (
            <Empty 
              description="No group mappings yet. Map groups to agent templates!"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        }}
      />

      <Modal
        title={editingMapping ? "✏️ Edit Group Mapping" : "➕ Map Group to Agent"}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Group ID <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              placeholder="e.g., 120363123456789012@g.us"
              value={formData.groupId}
              onChange={(e) => setFormData({ ...formData, groupId: e.target.value })}
              disabled={!!editingMapping}
            />
            <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Format: WhatsApp group ID with @g.us suffix
            </p>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Group Name (Optional)
            </label>
            <Input
              placeholder="e.g., Support Team"
              value={formData.groupName}
              onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Agent Template <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              style={{ width: '100%', padding: '8px', fontSize: 14, borderRadius: 4, border: '1px solid #d9d9d9' }}
              value={formData.templateId}
              onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
            >
              <option value="">Select an agent template</option>
              {templates.map(template => (
                <option key={template.templateId} value={template.templateId}>
                  {template.templateId} - {template.agentName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GroupMappingManager;

